import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Select } from '@qred/components-library/next';
import { ComboboxItem } from '@qred/components-library/dist/lib/next/components/Combobox/Combobox.types';
import {
  Banner,
  Body,
  Button,
  Card,
  Group,
  H6,
  Link,
  Loader,
  MaterialIcon,
  Stack,
  useWindowProperties,
  Highlight,
  Input,
  useModals,
  Radio,
  Label,
} from '@qred/components-library';
import { RootState } from '~/store';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import {
  ASPSP,
  PsuType,
  useGetAspspsQuery,
  useGetAllowedPsuTypesQuery,
  usePostAuthMutation,
} from '~/store/apis/endpoints/openBankingApi/openBanking.api';
import useTranslate from '~/hooks/useTranslate';
import { getAspspByName } from './BankConnection.utils';
import { ValidationContext } from '~/components/hoc/withValidation';
import { toggleGlobalErrorOn } from '~/store/slices/globalError.slice';
import { BankConnectionResult, GlobalErrorType } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import BankConnectionStatusModal from './components/BankConnectionStatusModal';
import { connectBankTermsAndConditionsLink } from '~/constants/markets';
import { openBankingMaxAspspsResultsToShow } from '~/constants/constVars';

/**
 * If {finalRedirectUrl} is provided, the flow will redirect the user to the specified URL after
 * successfully connecting, cancelling, or encountering an error. The result will be appended as a parameter to the URL.
 *
 * Note: The {onResult} prop cannot be provided if {finalRedirectUrl} is specified.
 */
interface RedirectFlowProps {
  finalRedirectUrl: string;
  onResult?: never;
  onBeforeRedirect?: () => Promise<void> | void;
}

/**
 * If {onResult} is provided, the flow will open a new popup window (tab if popup is blocked) and
 * show the BankConnectionStatusModal component. The result will be passed to the onResult callback.
 *
 * Note: The {finalRedirectUrl} prop can not be provided if onResult is provided.
 */
interface NewPageFlowProps {
  finalRedirectUrl?: never;
  onResult: (result: BankConnectionResult) => void;
  onBeforeRedirect?: never;
}

interface BankConnectionBaseProps {
  clientId: number;
  hasError?: boolean;
  allowCancel?: boolean;
  setError: (error: boolean) => void;
  showPsuTypeLabel?: boolean;
  /** When set, the search offers a "bank not listed" option that calls this instead of selecting a bank. */
  onManualEntrySelected?: () => void;
  searchHint?: React.ReactNode;
  /** Expandable help shown under the search field. */
  searchHelp?: { label: React.ReactNode; text: React.ReactNode };
  /**
   * Called with true once a search matches no bank while no bank is selected, and with false
   * when a later search matches, a bank is selected or the search is reset.
   */
  onNoSearchResultsChange?: (hasNoSearchResults: boolean) => void;
  /**
   * When set, the "Verify with your bank" button and its consent text are rendered
   * into this element (e.g. a step footer) instead of under the search.
   * Pass null while that element isn't mounted yet.
   */
  startAuthorizationContainer?: HTMLElement | null;
  /**
   * Set to false when the parent shows its own message after a failed or cancelled connection.
   * Hides the error banner, and the global error popup when starting the authorization fails.
   */
  showErrorMessages?: boolean;
}

type BankConnectionProps = BankConnectionBaseProps &
  (RedirectFlowProps | NewPageFlowProps);

const MANUAL_ENTRY_OPTION_VALUE = '__manual_entry__';

const filterBanksBySearch = (banks: ComboboxItem[], search: string) => {
  const normalizedSearch = search.toLowerCase().trim();
  return banks.filter((bank) =>
    bank.label.toLowerCase().includes(normalizedSearch)
  );
};

// Keeps the manual entry option last, also when no bank matches the search.
const filterBanksWithManualEntryOption = ({
  options,
  search,
  limit,
}: {
  options: ComboboxItem[];
  search: string;
  limit: number;
}) => {
  const banks = options.filter(
    (option) => option.value !== MANUAL_ENTRY_OPTION_VALUE
  );
  const manualEntryOptions = options.filter(
    (option) => option.value === MANUAL_ENTRY_OPTION_VALUE
  );
  return [
    ...filterBanksBySearch(banks, search).slice(0, limit),
    ...manualEntryOptions,
  ];
};

const BankConnection = ({
  clientId,
  hasError,
  finalRedirectUrl,
  allowCancel = true,
  setError,
  onResult,
  showPsuTypeLabel,
  onBeforeRedirect,
  onManualEntrySelected,
  searchHint,
  searchHelp,
  onNoSearchResultsChange,
  startAuthorizationContainer,
  showErrorMessages = true,
}: BankConnectionProps) => {
  const shouldStayInSameTab = Boolean(finalRedirectUrl);
  const [selectedASPSP, setSelectedASPSP] = useState<ASPSP>();
  const [selectedPSU, setSelectedPSU] = useState<PsuType>();
  const dispatch = useAppDispatch();
  const translate = useTranslate();
  const { isMobile } = useWindowProperties();
  const startAuthenticationContainerRef = useRef<HTMLDivElement>(null);
  const aspspsSelectRef = useRef<HTMLInputElement>(null);
  const {
    intl: { language, market },
  } = useSelector((state: RootState) => state);
  const {
    data: aspsps = [],
    isFetching: getAspspsIsFetching,
    isError: getAspspsIsError,
  } = useGetAspspsQuery(
    { psuType: selectedPSU! },
    {
      skip: !selectedPSU,
    }
  );
  const {
    data: allowedPsuTypesData,
    isFetching: getAllowedPsuTypesIsFetching,
    isError: getAllowedPsuTypesIsError,
  } = useGetAllowedPsuTypesQuery(
    { clientId },
    {
      skip: !clientId,
    }
  );
  const [
    postAuth,
    { isLoading: isAuthLoading, isError: isPostAuthError },
  ] = usePostAuthMutation();
  const [searchValue, setSearchValue] = useState<string>('');
  const [isBankNotFound, setIsBankNotFound] = useState(false);
  const validationContext = useContext(ValidationContext);
  const modals = useModals();

  const openBankingLocale = language.split('_')[0];

  // The backend decides which PSU types the client may use (sole traders may
  // choose, other company types are business only). If the lookup fails we
  // fall back to letting the user choose, which was the previous behavior.
  const allowedPsuTypes: PsuType[] = getAllowedPsuTypesIsError
    ? ['business', 'personal']
    : allowedPsuTypesData?.allowedPsuTypes ?? [];
  const showPsuTypeChoice = allowedPsuTypes.length > 1;

  const allPsuTypeOptions: Array<{ value: PsuType; label: string }> = [
    {
      value: 'business',
      label: translate('BankConnection.PsuTypeBusiness') as string,
    },
    {
      value: 'personal',
      label: translate('BankConnection.PsuTypePersonal') as string,
    },
  ];
  const psuTypeOptions = allPsuTypeOptions.filter((option) =>
    allowedPsuTypes.includes(option.value)
  );

  useEffect(() => {
    if (allowedPsuTypes.length === 1 && selectedPSU !== allowedPsuTypes[0]) {
      setSelectedPSU(allowedPsuTypes[0]);
    }
  }, [allowedPsuTypes, selectedPSU]);

  useEffect(() => {
    const startAuthenticationContainerElement =
      startAuthenticationContainerRef?.current;
    if (startAuthenticationContainerElement) {
      startAuthenticationContainerElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [selectedASPSP]);

  useEffect(() => {
    const aspspsSelectElement = aspspsSelectRef?.current;
    if (aspspsSelectElement) {
      aspspsSelectElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [aspspsSelectRef]);

  useEffect(() => {
    if (selectedASPSP) {
      validationContext.addPropertyToValidationErrors(
        'BankSelectedButNotConnected'
      );
    }

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'BankSelectedButNotConnected'
      );
    };
  }, [selectedASPSP]);

  useEffect(() => {
    setSelectedASPSP(undefined);
  }, [getAspspsIsFetching]);

  useEffect(() => {
    if (getAspspsIsError || isPostAuthError) {
      onResult && onResult(BankConnectionResult.Error);
    }
  }, [getAspspsIsError, isPostAuthError]);

  const resetHasError = () => {
    setError(false);
  };

  const handlePsuTypeSelect = (psuType: PsuType) => {
    setSelectedPSU(psuType);
    resetInputField();

    if (hasError) {
      resetHasError();
    }
  };

  const handleASPSPSelect = (aspspName: string) => {
    const aspsp = getAspspByName(aspsps, aspspName);
    setSelectedASPSP(aspsp);

    if (hasError) {
      resetHasError();
    }
  };

  const handleOptionSelect = (value: string) => {
    if (value === MANUAL_ENTRY_OPTION_VALUE) {
      onManualEntrySelected?.();
      return;
    }

    handleASPSPSelect(value);
  };

  const handleConnectionResult = (result: BankConnectionResult) => {
    onResult && onResult(result);
    resetInputField();
    if (
      result === BankConnectionResult.Error ||
      result === BankConnectionResult.Cancelled
    ) {
      setError(true);
    }
  };

  const handleStartAuthorization = async () => {
    if (!clientId || !selectedASPSP || !selectedPSU) {
      logToSentry(
        new Error('BankConnection: clientId or selectedASPSP is missing'),
        'handleStartAuthorization'
      );
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      return;
    }

    try {
      const { authorizationUrl, state: sessionState } = await postAuth({
        body: {
          aspsp: {
            name: selectedASPSP.name,
            country: selectedASPSP.country,
          },
          clientId,
          redirectUrl: finalRedirectUrl,
          language: openBankingLocale,
          maximumConsentValidity: selectedASPSP.maximumConsentValidity,
          psuType: selectedPSU,
        },
      }).unwrap();

      if (shouldStayInSameTab) {
        await onBeforeRedirect?.();
        location.assign(authorizationUrl);
      } else {
        const popupWindow = window.open(authorizationUrl, '_blank', 'popup');

        if (!popupWindow) {
          console.log(
            'Window.open for a popup blocked, opening new tab instead'
          );
          const newTab = window.open(authorizationUrl, '_blank');

          if (!newTab) {
            console.log(
              'Window.open for a new tab blocked, opening tab with a faked click event instead'
            );
            const link = document.createElement('a');
            link.href = authorizationUrl;
            link.target = '_blank';
            link.click();
          }
        }

        modals.openModal({
          id: 'bankConnectionStatusModal',
          children: (
            <BankConnectionStatusModal
              id="bankConnectionStatusModal"
              sessionState={sessionState}
              onResult={handleConnectionResult}
              authorizationUrl={authorizationUrl}
            />
          ),
          withCloseButton: false,
          closeOnClickOutside: false,
          closeOnEscape: false,
        });
      }
    } catch (error) {
      if (showErrorMessages) {
        dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      }
      console.error('Error while starting authorization', error);
    }
  };

  const resetInputField = () => {
    setSearchValue('');
    setSelectedASPSP(undefined);
    setIsBankNotFound(false);
  };

  const options: ComboboxItem[] = useMemo(
    () =>
      aspsps.map(({ name }) => ({
        value: name,
        label: name,
      })),
    [aspsps]
  );

  const shouldNotDisplayAspspsOptionsWithoutSearch =
    options.length > openBankingMaxAspspsResultsToShow;

  const hasSearchValue = searchValue.trim() !== '';
  const hasNoSearchResults =
    hasSearchValue &&
    !getAspspsIsFetching &&
    filterBanksBySearch(options, searchValue).length === 0;
  const showManualEntryOption =
    Boolean(onManualEntrySelected) && hasSearchValue;
  const manualEntryOption: ComboboxItem = {
    value: MANUAL_ENTRY_OPTION_VALUE,
    label: translate(
      hasNoSearchResults
        ? 'BankConnection.ManualEntryOptionNoResults'
        : 'BankConnection.ManualEntryOption'
    ) as string,
  };
  const optionsWithManualEntry = showManualEntryOption
    ? [...options, manualEntryOption]
    : options;
  const showBankNotFoundMessage =
    Boolean(onManualEntrySelected) && isBankNotFound && !selectedASPSP;

  // The select clears the search on blur, so an empty search keeps the not-found state until the next search.
  useEffect(() => {
    if (!hasSearchValue || getAspspsIsFetching) {
      return;
    }
    setIsBankNotFound(hasNoSearchResults);
  }, [hasSearchValue, hasNoSearchResults, getAspspsIsFetching]);

  // With a bank selected, the select puts its name back in the search on blur, so the not-found state would flicker
  useEffect(() => {
    onNoSearchResultsChange?.(isBankNotFound && !selectedASPSP);
  }, [isBankNotFound, selectedASPSP]);

  useEffect(() => () => onNoSearchResultsChange?.(false), []);

  const hasStartAuthorizationInContainer =
    startAuthorizationContainer !== undefined;

  const startAuthorizationButton = (
    <Button
      onClick={handleStartAuthorization}
      leftIcon="verified_user"
      isLoading={isAuthLoading}
      disabled={!selectedASPSP}
      size="md"
      fullWidth={isMobile}
    >
      <span data-record>
        {translate('BankConnection.StartAuthenticationButtonText')}
      </span>
    </Button>
  );

  const getConsentTexts = (align: 'left' | 'center') => (
    <>
      <Body size="sm" align={align} data-record>
        {translate('BankConnection.ConnectionExplanation')}
      </Body>
      <Body size="sm" align={align} data-record>
        {translate('BankConnection.ConsentText', {
          startAuthenticationButtonText: translate(
            'BankConnection.StartAuthenticationButtonText'
          ),
          termsLinkText: (
            <a
              href={connectBankTermsAndConditionsLink[market][language]}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Link size="sm" data-record>
                {translate('BankConnection.TermsLinkText')}
              </Link>
            </a>
          ),
        })}{' '}
        {allowCancel &&
          translate('BankConnection.CancelText', {
            cancel: translate('Cancel'),
          })}
      </Body>
    </>
  );

  return (
    <Stack spacing="xxl">
      {getAllowedPsuTypesIsFetching && <Loader />}
      {!getAllowedPsuTypesIsFetching && showPsuTypeChoice && (
        <Stack spacing={'sm'}>
          {showPsuTypeLabel && (
            <Label data-record>
              {translate('BankConnection.PsuTypeLabel')}
            </Label>
          )}
          <Radio.Group onChange={handlePsuTypeSelect} value={selectedPSU}>
            {psuTypeOptions.map((psuType) => (
              <Radio
                key={psuType.value}
                label={<span data-record>{psuType.label}</span>}
                value={psuType.value}
              />
            ))}
          </Radio.Group>
        </Stack>
      )}
      {selectedPSU && (
        <>
          <Stack spacing={4}>
            <Select
              ref={aspspsSelectRef}
              data={
                getAspspsIsFetching ||
                (shouldNotDisplayAspspsOptionsWithoutSearch &&
                  !searchValue.trim())
                  ? []
                  : optionsWithManualEntry
              }
              filter={
                showManualEntryOption
                  ? filterBanksWithManualEntryOption
                  : undefined
              }
              limit={openBankingMaxAspspsResultsToShow}
              label={
                <span data-record>
                  {translate('BankConnection.SearchLabel')}
                </span>
              }
              searchable
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              value={selectedASPSP?.name ?? ''}
              onChange={handleOptionSelect}
              allowDeselect={false}
              withScrollArea
              nothingFoundMessage={
                <>
                  {getAspspsIsFetching ? (
                    <Loader />
                  ) : (
                    <span data-record>
                      {searchValue.trim()
                        ? translate('BankConnection.SearchNoBanksFound')
                        : translate('BankConnection.SearchStartTyping')}
                    </span>
                  )}
                </>
              }
              leftSection={<MaterialIcon src="search" />}
              rightSection={
                selectedASPSP ? (
                  <img
                    src={`${selectedASPSP.logo}-/resize/x16/`}
                    alt={`${selectedASPSP.name} logo`}
                    height="16"
                  />
                ) : (
                  <></>
                )
              }
              renderOption={({ option }) => {
                if (option.value === MANUAL_ENTRY_OPTION_VALUE) {
                  return (
                    <Group align="center" spacing="xs">
                      <MaterialIcon src="search_off" />
                      <Link component="span" data-record underline={false}>
                        {option.label}
                      </Link>
                    </Group>
                  );
                }

                const aspsp = getAspspByName(aspsps, option.value);

                return (
                  <Group align="center" justify="space-between">
                    <Highlight highlight={searchValue} data-record>
                      {aspsp.name}
                    </Highlight>
                    <img
                      src={`${aspsp.logo}-/resize/x16/`}
                      alt={`${aspsp.name} logo`}
                      height={14}
                      loading="lazy"
                    />
                  </Group>
                );
              }}
            />
            {/* Rendered here instead of as Select props, so the hint stays above the expandable help */}
            {searchHint && <Input.Hint data-record>{searchHint}</Input.Hint>}
            {searchHelp && (
              <Input.Description collapseLabel={searchHelp.label}>
                <Body size="sm" data-record>
                  {searchHelp.text}
                </Body>
              </Input.Description>
            )}
          </Stack>

          {showBankNotFoundMessage && (
            <Card
              color="Container/Option_5_(Greige_II)"
              px="xxl"
              py={40}
              style={{ borderRadius: '8px' }}
            >
              <Stack spacing="sm" align="center">
                <Group spacing="sm" align="center" justify="center">
                  <MaterialIcon src="search_off" size={32} />
                  <H6 color="Text/Headlines_(Brand)" data-record>
                    {translate('BankConnection.BankNotFoundTitle')}
                  </H6>
                </Group>
                <Body size="lg" align="center" data-record>
                  {translate('BankConnection.BankNotFoundDescription')}
                </Body>
              </Stack>
            </Card>
          )}

          {selectedASPSP && !hasStartAuthorizationInContainer && (
            <Stack spacing="xl" ref={startAuthenticationContainerRef}>
              <Stack spacing="lg" align={isMobile ? 'stretch' : 'start'}>
                <Group>
                  {startAuthorizationButton}
                  {allowCancel && (
                    <Button
                      variant="tertiary"
                      size="md"
                      onClick={resetInputField}
                      fullWidth={isMobile}
                    >
                      <span data-record>{translate('Cancel')}</span>
                    </Button>
                  )}
                </Group>
                {getConsentTexts('left')}
              </Stack>
            </Stack>
          )}
        </>
      )}
      {hasError && showErrorMessages && (
        <Banner
          opened
          withCloseButton={false}
          message={
            <span data-record>
              {translate('BankConnection.AuthenticationFlowErrorOrCancelled')}
            </span>
          }
          state="informative"
        />
      )}
      {startAuthorizationContainer &&
        createPortal(
          <Stack spacing="sm" align="center">
            {selectedASPSP && getConsentTexts('center')}
            {startAuthorizationButton}
          </Stack>,
          startAuthorizationContainer
        )}
    </Stack>
  );
};

export default BankConnection;
